import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Payment as PaymentIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { PaymentHistoryItem } from "../../../redux/types/types";
import { PaymentFailureDetails } from "./PaymentFailureDetails";
import AttendeeImportantInfoButton from "../../Attendees/AttendeeImportantInfoButton";

interface FailedPayment extends PaymentHistoryItem {
  stripeSessionId?: string;
  stripePaymentIntentId?: string;
  responseCode?: string;
  responseMessage?: string;
  webhookLogs?: Array<{
    id: string;
    status: string;
    responseCode: string;
    responseMessage: string;
    createdAt: string;
    signatureValid: boolean;
  }>;
}

interface FailedPaymentsManagementProps {
  payments: FailedPayment[];
  loading: boolean;
  onRefresh: () => void;
  onRetryPayment: (paymentId: string) => Promise<void>;
  onMarkAsResolved: (paymentId: string, notes: string) => Promise<void>;
  onRefundPayment: (paymentId: string, reason: string) => Promise<void>;
  onClosePayment: (paymentId: string) => Promise<void>;
}

export default function FailedPaymentsManagement({
  payments,
  loading,
  onRefresh,
  onRetryPayment,
  onMarkAsResolved,
  onRefundPayment,
  onClosePayment,
}: FailedPaymentsManagementProps) {
  const intl = useIntl();

  const [selectedPayment, setSelectedPayment] = useState<FailedPayment | null>(
    null,
  );
  const [dialogType, setDialogType] = useState<
    "details" | "resolve" | "refund" | "close" | null
  >(null);
  const [notes, setNotes] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "FAILED":
        return "error";
      case "PENDING":
        return "warning";
      case "EXPIRED":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "FAILED":
        return <ErrorIcon fontSize="small" />;
      case "SUCCESS":
        return <SuccessIcon fontSize="small" />;
      case "PENDING":
        return <InfoIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleRetryPayment = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      await onRetryPayment(selectedPayment.id);
      setDialogType(null);
      setSelectedPayment(null);
    } catch (error) {
      console.error("Failed to retry payment:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkAsResolved = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      await onMarkAsResolved(selectedPayment.id, notes);
      setDialogType(null);
      setSelectedPayment(null);
      setNotes("");
    } catch (error) {
      console.error("Failed to mark as resolved:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      await onRefundPayment(selectedPayment.id, refundReason);
      setDialogType(null);
      setSelectedPayment(null);
      setRefundReason("");
    } catch (error) {
      console.error("Failed to refund payment:", error);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = async () => {
    if (!selectedPayment) return;

    setProcessing(true);
    try {
      await onClosePayment(selectedPayment.id);
      setDialogType(null);
      setSelectedPayment(null);
    } catch (error) {
      console.error("Failed to close payment:", error);
    } finally {
      setProcessing(false);
    }
  };

  const failedPayments = payments.filter(
    (p) =>
      p.status === "FAILED" || p.status === "EXPIRED" || p.status === "PENDING",
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          <FormattedMessage id="failedPayments.title" />
        </Typography>
        <Tooltip
          title={intl.formatMessage({ id: "admin.failedPayments.refresh" })}
        >
          <IconButton onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : failedPayments.length === 0 ? (
        <Alert severity="success">
          <FormattedMessage
            id="admin.failedPayments.noFailedPayments"
            defaultMessage="No failed payments found. All Stripe payments are processing successfully!"
          />
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {failedPayments.map((payment) => (
            <Grid item xs={12} key={payment.id}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {`${payment.attendee.firstName} ${payment.attendee.lastName}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {payment.attendee.email}
                      </Typography>
                      <AttendeeImportantInfoButton
                        attendee={payment.attendee}
                      />
                      <Typography variant="body2" color="text.secondary">
                        <FormattedMessage
                          id="admin.failedPayments.created"
                          defaultMessage="Created:"
                        />{" "}
                        {formatDateTime(payment.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        icon={getStatusIcon(payment.status)}
                        label={payment.status}
                        color={getStatusColor(payment.status) as any}
                        size="small"
                      />
                      <Typography variant="h6" color="primary">
                        {formatAmount(payment.amount)} {payment.currency}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>
                          <FormattedMessage
                            id="admin.failedPayments.classes"
                            defaultMessage="Classes:"
                          />
                        </strong>{" "}
                        {payment.numberOfClasses || (
                          <FormattedMessage
                            id="admin.failedPayments.notAvailable"
                            defaultMessage="N/A"
                          />
                        )}
                      </Typography>
                      {payment.specialType && (
                        <Typography variant="body2">
                          <strong>
                            <FormattedMessage
                              id="admin.failedPayments.specialType"
                              defaultMessage="Special Type:"
                            />
                          </strong>{" "}
                          {payment.specialType}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {payment.responseMessage && (
                        <Typography variant="body2" color="error">
                          <strong>
                            <FormattedMessage
                              id="admin.failedPayments.error"
                              defaultMessage="Error:"
                            />
                          </strong>{" "}
                          {payment.responseMessage}
                        </Typography>
                      )}
                      {payment.responseCode && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>
                            <FormattedMessage
                              id="admin.failedPayments.code"
                              defaultMessage="Code:"
                            />
                          </strong>{" "}
                          {payment.responseCode}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={() => {
                        setSelectedPayment(payment);
                        setDialogType("details");
                      }}
                    >
                      <FormattedMessage
                        id="admin.failedPayments.viewDetails"
                        defaultMessage="View Details"
                      />
                    </Button>

                    {payment.status === "FAILED" && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<PaymentIcon />}
                        onClick={() => {
                          setSelectedPayment(payment);
                          handleRetryPayment();
                        }}
                      >
                        <FormattedMessage
                          id="admin.failedPayments.retry"
                          defaultMessage="Retry Payment"
                        />
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setDialogType("resolve");
                      }}
                    >
                      <FormattedMessage
                        id="admin.failedPayments.markResolved"
                        defaultMessage="Mark as Resolved"
                      />
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        setSelectedPayment(payment);
                        setDialogType("close");
                      }}
                    >
                      <FormattedMessage
                        id="admin.failedPayments.close"
                        defaultMessage="Close"
                      />
                    </Button>

                    {(payment.status === "SUCCESS" ||
                      payment.status === "FAILED") && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setSelectedPayment(payment);
                          setDialogType("refund");
                        }}
                      >
                        <FormattedMessage
                          id="admin.failedPayments.refund"
                          defaultMessage="Process Refund"
                        />
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Payment Details Dialog */}
      <Dialog
        open={dialogType === "details"}
        onClose={() => setDialogType(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage
            id="admin.failedPayments.paymentDetails"
            defaultMessage="Payment Details"
          />
        </DialogTitle>
        <DialogContent>
          {selectedPayment && (
            <PaymentFailureDetails
              payment={selectedPayment}
              formatAmount={formatAmount}
              formatDateTime={formatDateTime}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>
            <FormattedMessage
              id="admin.failedPayments.close"
              defaultMessage="Close"
            />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Resolved Dialog */}
      <Dialog
        open={dialogType === "resolve"}
        onClose={() => setDialogType(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage
            id="admin.failedPayments.markAsResolved"
            defaultMessage="Mark Payment as Resolved"
          />
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <FormattedMessage
              id="admin.failedPayments.addResolutionNotes"
              defaultMessage="Add notes about how this payment issue was resolved:"
            />
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={intl.formatMessage({
              id: "admin.failedPayments.resolutionNotes",
              defaultMessage: "Resolution Notes",
            })}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={intl.formatMessage({
              id: "admin.failedPayments.resolutionPlaceholder",
              defaultMessage: "Describe the resolution steps taken...",
            })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>
            <FormattedMessage
              id="admin.failedPayments.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button
            onClick={handleMarkAsResolved}
            variant="contained"
            disabled={!notes.trim() || processing}
            startIcon={processing ? <CircularProgress size={16} /> : null}
          >
            <FormattedMessage
              id="admin.failedPayments.markResolved"
              defaultMessage="Mark as Resolved"
            />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog
        open={dialogType === "refund"}
        onClose={() => setDialogType(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage
            id="admin.failedPayments.refundPayment"
            defaultMessage="Refund Payment"
          />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage
              id="admin.failedPayments.refundConfirmation"
              defaultMessage="Are you sure you want to refund this payment?"
            />
          </Typography>
          {selectedPayment && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.id"
                  defaultMessage="Payment ID"
                />
                : {selectedPayment.id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.amount"
                  defaultMessage="Amount"
                />
                : {formatAmount(selectedPayment.amount)} CHF
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.status"
                  defaultMessage="Status"
                />
                : {selectedPayment.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleRefund}
            variant="contained"
            color="warning"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={20} />
            ) : (
              <FormattedMessage
                id="admin.failedPayments.refund"
                defaultMessage="Refund"
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Dialog */}
      <Dialog
        open={dialogType === "close"}
        onClose={() => setDialogType(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <FormattedMessage
            id="admin.failedPayments.closePayment"
            defaultMessage="Close Payment"
          />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage
              id="admin.failedPayments.closeConfirmation"
              defaultMessage="Are you sure you want to close this payment? This will mark it as closed and it will no longer be treated as failed or successful."
            />
          </Typography>
          {selectedPayment && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.id"
                  defaultMessage="Payment ID"
                />
                : {selectedPayment.id}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.amount"
                  defaultMessage="Amount"
                />
                : {formatAmount(selectedPayment.amount)} CHF
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="admin.payments.status"
                  defaultMessage="Status"
                />
                : {selectedPayment.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogType(null)}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={handleClose}
            variant="contained"
            color="secondary"
            disabled={processing}
          >
            {processing ? (
              <CircularProgress size={20} />
            ) : (
              <FormattedMessage
                id="admin.failedPayments.close"
                defaultMessage="Close"
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
